import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import remarkEmoji from 'remark-emoji';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getPlatformIcon } from './iconUtils';




// Custom preprocessor for [url=scheme://host]text[/url] syntax
const preprocessCustomSyntax = (text) => {
  if (!text) return text;
  
  // Replace [url=scheme://host]text[/url] with standard markdown links
  return text.replace(/\[url=([^\]]+)\]([^\[]+)\[\/url\]/g, '[$2]($1)');
};

// Enhanced markdown parser with comprehensive features
export const parseMarkdown = (text, options = {}) => {
  if (!text) return null;
  
  const processedText = preprocessCustomSyntax(text);
  
  const defaultComponents = {
    // Enhanced link handling with security and platform icons
    a: ({ href, children, ...props }) => {
      const icon = getPlatformIcon(href);
      
      return (
        <a 
          href={href} 
          target="_blank" 
          rel="noopener noreferrer"
          {...props}
        >
          <FontAwesomeIcon icon={icon} className="platform-icon" />
          {children}
        </a>
      );
    },
    
    // Enhanced code blocks
    code: ({ inline, className, children, ...props }) => {
      const match = /language-(\w+)/.exec(className || '');
      return !inline ? (
        <pre className="code-block">
          <code className={className} {...props}>
            {children}
          </code>
        </pre>
      ) : (
        <code className="inline-code" {...props}>
          {children}
        </code>
      );
    },
    
    // Enhanced blockquotes
    blockquote: ({ children, ...props }) => (
      <blockquote className="markdown-blockquote" {...props}>
        {children}
      </blockquote>
    ),
    
    // Enhanced tables
    table: ({ children, ...props }) => (
      <div className="table-wrapper">
        <table className="markdown-table" {...props}>
          {children}
        </table>
      </div>
    ),
    
    // Enhanced lists
    ul: ({ children, ...props }) => (
      <ul className="markdown-list" {...props}>
        {children}
      </ul>
    ),
    
    ol: ({ children, ...props }) => (
      <ol className="markdown-list ordered" {...props}>
        {children}
      </ol>
    ),
    
    // Enhanced headings
    h1: ({ children, ...props }) => (
      <h1 className="markdown-heading h1" {...props}>
        {children}
      </h1>
    ),
    
    h2: ({ children, ...props }) => (
      <h2 className="markdown-heading h2" {...props}>
        {children}
      </h2>
    ),
    
    h3: ({ children, ...props }) => (
      <h3 className="markdown-heading h3" {...props}>
        {children}
      </h3>
    ),
    
    h4: ({ children, ...props }) => (
      <h4 className="markdown-heading h4" {...props}>
        {children}
      </h4>
    ),
    
    h5: ({ children, ...props }) => (
      <h5 className="markdown-heading h5" {...props}>
        {children}
      </h5>
    ),
    
    h6: ({ children, ...props }) => (
      <h6 className="markdown-heading h6" {...props}>
        {children}
      </h6>
    ),
    
    // Enhanced paragraphs
    p: ({ children, ...props }) => (
      <p className="markdown-paragraph" {...props}>
        {children}
      </p>
    ),
    
    // Enhanced images
    img: ({ src, alt, ...props }) => (
      <img 
        src={src} 
        alt={alt} 
        className="markdown-image"
        loading="lazy"
        {...props}
      />
    ),
    
    // Enhanced horizontal rules
    hr: ({ ...props }) => (
      <hr className="markdown-divider" {...props} />
    ),
    
    ...options.components
  };
  
  return (
    <ReactMarkdown
      remarkPlugins={[
        remarkGfm,        // GitHub Flavored Markdown (tables, strikethrough, task lists, etc.)
        remarkBreaks,     // Convert line breaks to <br> tags
        remarkEmoji,      // Convert :emoji: syntax to actual emojis
        ...(options.remarkPlugins || [])
      ]}
      rehypePlugins={[
        ...(options.rehypePlugins || [])
      ]}
      components={defaultComponents}
      skipHtml={false}
      {...options}
    >
      {processedText}
    </ReactMarkdown>
  );
};

// Convenience function for recap text specifically
export const renderRecapText = (text) => {
  return parseMarkdown(text, {
    components: {
      // Enhanced link handling with platform icons for recap
      a: ({ href, children, ...props }) => {
        const icon = getPlatformIcon(href);
        
        return (
          <a 
            href={href} 
            target="_blank" 
            rel="noopener noreferrer"
            {...props}
          >
            <FontAwesomeIcon icon={icon} className="platform-icon" />
            {children}
          </a>
        );
      },
      
      // Additional styling for recap-specific content
      p: ({ children, ...props }) => (
        <p className="recap-paragraph" {...props}>
          {children}
        </p>
      ),
      
      h1: ({ children, ...props }) => (
        <h1 className="recap-heading recap-h1" {...props}>
          {children}
        </h1>
      ),
      
      h2: ({ children, ...props }) => (
        <h2 className="recap-heading recap-h2" {...props}>
          {children}
        </h2>
      ),
      
      h3: ({ children, ...props }) => (
        <h3 className="recap-heading recap-h3" {...props}>
          {children}
        </h3>
      ),
      
      ul: ({ children, ...props }) => (
        <ul className="recap-list" {...props}>
          {children}
        </ul>
      ),
      
      ol: ({ children, ...props }) => (
        <ol className="recap-list ordered" {...props}>
          {children}
        </ol>
      ),
      
      blockquote: ({ children, ...props }) => (
        <blockquote className="recap-quote" {...props}>
          {children}
        </blockquote>
      )
    }
  });
};

export default { parseMarkdown, renderRecapText };